import { useCallback, useState } from "react";
import type { PulseMetric } from "../../CompanyPulseCard";
import {
  DEFAULT_SORT_DIR,
  WatchlistSortDir,
  WatchlistSortKey,
} from "../sortWatchlistEntries";

// Per-watchlist view preference — how each list is sorted and which metric
// its cards show. A per-DEVICE preference in localStorage (the
// useSelectedWatchlist precedent), keyed by watchlist id so a dividend list
// and a momentum list can hold different sorts (Ben, 2026-09-01). Read
// SYNCHRONOUSLY in the useState initializer so the very first render — and
// therefore the very first paint of the fetched list — is already in the
// chosen order; hydrating in an effect would flash the default order first.

const STORAGE_KEY = "insight:watchlist-view-prefs";

export interface WatchlistViewPref {
  sort: WatchlistSortKey;
  dir: WatchlistSortDir;
  show: PulseMetric;
}

export const DEFAULT_VIEW_PREF: WatchlistViewPref = {
  sort: "manual",
  dir: "desc",
  show: "changePct",
};

const SORT_KEYS = new Set<string>(Object.keys(DEFAULT_SORT_DIR));
const SHOW_KEYS = new Set<string>(["changePct", "change", "marketCap"]);

// Storage is input, never truth: entries that don't pass the enum whitelist
// are dropped whole, so a stale or hand-edited value can only ever fall
// back to the default, not crash a comparator.
const parsePref = (value: unknown): WatchlistViewPref | null => {
  if (typeof value !== "object" || value === null) return null;
  const { sort, dir, show } = value as Record<string, unknown>;
  if (typeof sort !== "string" || !SORT_KEYS.has(sort)) return null;
  if (dir !== "asc" && dir !== "desc") return null;
  if (typeof show !== "string" || !SHOW_KEYS.has(show)) return null;
  return { sort: sort as WatchlistSortKey, dir, show: show as PulseMetric };
};

const readStoredPrefs = (): Record<string, WatchlistViewPref> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    const result: Record<string, WatchlistViewPref> = {};
    for (const [watchlistId, value] of Object.entries(parsed)) {
      // Real ids are UUIDs; a hand-edited "__proto__" key would otherwise
      // set the result object's prototype via the index assignment.
      if (watchlistId === "__proto__" || watchlistId === "constructor") continue;
      const pref = parsePref(value);
      if (pref) result[watchlistId] = pref;
    }
    return result;
  } catch {
    // Storage unavailable (private mode) — sorts just won't persist.
    return {};
  }
};

interface WatchlistViewPrefs extends WatchlistViewPref {
  setSortPref: (sort: WatchlistSortKey, dir: WatchlistSortDir) => void;
  setShowPref: (show: PulseMetric) => void;
}

export const useWatchlistViewPrefs = (watchlistId: string | null): WatchlistViewPrefs => {
  const [prefs, setPrefs] = useState<Record<string, WatchlistViewPref>>(readStoredPrefs);

  const update = useCallback(
    (patch: Partial<WatchlistViewPref>) => {
      if (!watchlistId) return;
      setPrefs((prev) => {
        const next = { ...DEFAULT_VIEW_PREF, ...prev[watchlistId], ...patch };
        // Write-through, merged over the LIVE stored map — never a wholesale
        // rewrite from this instance's state. localStorage is shared across
        // tabs, so replaying our mount-time snapshot would clobber anything
        // another dashboard tab saved since. Only the entry the user just
        // changed is written. (Living inside the updater keeps prev exact;
        // a StrictMode double-invoke just writes the same bytes twice.)
        try {
          const stored = readStoredPrefs();
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ ...stored, [watchlistId]: next })
          );
        } catch {
          // The preference just won't survive to the next launch.
        }
        return { ...prev, [watchlistId]: next };
      });
    },
    [watchlistId]
  );

  const setSortPref = useCallback(
    (sort: WatchlistSortKey, dir: WatchlistSortDir) => update({ sort, dir }),
    [update]
  );
  const setShowPref = useCallback((show: PulseMetric) => update({ show }), [update]);

  const pref = (watchlistId ? prefs[watchlistId] : undefined) ?? DEFAULT_VIEW_PREF;
  return { ...pref, setSortPref, setShowPref };
};
